/**
 * The last few of something, held strongly.
 *
 * Both registries in this app hold their models through `WeakRef`, which is
 * right for the hundreds of pairs a feed resolves and shows once: when the
 * list lets go, the models go with it. It is wrong for the one pair the
 * person is actually looking at, because nothing else in the app holds that
 * model either — a resource keeps it in its state only while it is connected,
 * and clears it on disconnect — so between one component asking and the next
 * the model can simply be collected.
 *
 * Measured on staging, loading a pair page from cold: `services/pair-info`
 * went out three times for the same pair, at 1,391ms, 3,215ms and 4,405ms.
 * Each asker checked the registry first and each one missed.
 *
 * So the last few are kept strongly, and everything older stays weak. Small
 * on purpose: this is for the pair on screen and the handful behind it in the
 * history, not for a feed.
 */
export const createRecentCache = <V>({ size }: { size: number }) => {
  /** Insertion order is the recency order: oldest first. */
  const entries = new Map<string, V>()

  const touch = (key: string, value: V) => {
    entries.delete(key)
    entries.set(key, value)
  }

  const cache = {
    get: (key: string) => {
      const value = entries.get(key)
      if (value !== undefined) {
        touch(key, value)
      }
      return value
    },

    set: (key: string, value: V) => {
      touch(key, value)
      while (entries.size > size) {
        const oldest = entries.keys().next()
        if (oldest.done) {
          return
        }
        entries.delete(oldest.value)
      }
    },

    /** `factory` runs only when the key is not held. */
    getOrCreate: (key: string, factory: () => V) => {
      const existing = cache.get(key)
      if (existing !== undefined) {
        return existing
      }
      const created = factory()
      cache.set(key, created)
      return created
    },

    /** How many are held. For tests and for debugging. */
    get count() {
      return entries.size
    },
  }

  return cache
}

export type RecentCache<V> = ReturnType<typeof createRecentCache<V>>
